import { useState } from 'react'
import { messageOf } from '../../lib/errors'
import { preparePhoto } from '../../lib/photos'
import { useShopImage } from '../../lib/useShopImage'
import {
  setShopImage, uploadShopImage, imageSourceLabel,
  type ManagedItem, type ImageTarget,
} from '../../lib/shop'

/**
 * Pictures, at the three levels 070 built. REC-25.
 *
 * ── THE MIDDLE LEVEL IS THE WHOLE DESIGN ────────────────────────────────────
 *
 * David, 14 Sep: "a product default image and then a default per variant type
 * and per variant as optional. So for the color variant, we'll probably include
 * a photo for each, but for the size we won't."
 *
 * **A PHOTOGRAPH VARIES WITH SOME AXES AND NOT OTHERS.** Colour changes what
 * the thing looks like; size does not. So the screen offers an upload against
 * every OPTION and expects most of them to stay empty — the twenty-combination
 * shirt takes two photographs, one per colour, and nobody shoots a navy medium
 * separately from a navy large.
 *
 * ── WHICH PICTURE WINS IS NOT DECIDED HERE ──────────────────────────────────
 *
 * `app.shop_image_for` ranks variant → option → item and breaks a tie on axis
 * POSITION. This screen only ever RENDERS what the server resolved and says
 * where it came from. A second ranking in the browser would disagree the first
 * time somebody reorders an axis (REC-29), and the manager would be looking at
 * a different photograph from the one the shop floor shows.
 *
 * That is also why every combination row says its source in words. Four rows
 * showing the same navy photograph is correct and looks like a bug until the
 * row says "From Navy" — and a manager who cannot tell where a picture came
 * from cannot find the control that changes it.
 */
export function ItemPictures({
  item,
  tenantId,
  onError,
  onChanged,
}: {
  item: ManagedItem
  tenantId: string
  onError: (m: string | null) => void
  onChanged: () => void
}) {
  if (!item.iMayEdit) return null

  const photographable = item.axes.filter((a) => a.options.length > 0)

  return (
    <div className="pics">
      <h4 className="shopitems__subhead">Pictures</h4>

      <div className="pics__row">
        <ImageSlot
          label="Default"
          hint="Shown wherever nothing more specific applies."
          target={{ itemId: item.itemId }}
          path={item.image?.path ?? null}
          tenantId={tenantId}
          itemId={item.itemId}
          onError={onError}
          onChanged={onChanged}
        />
      </div>

      {photographable.length > 0 && (
        <>
          <p className="admin__hint">
            Add a picture to the options that change what it LOOKS like — the
            colours. Leave the sizes and the fits empty; they are the same
            photograph, and one per colour is two uploads instead of{' '}
            {item.variants.filter((v) => v.isActive).length}.
          </p>
          {photographable.map((axis) => (
            <div key={axis.axisId} className="pics__axis">
              <p className="axes__addlabel">{axis.label}</p>
              <div className="pics__row">
                {axis.options.map((o) => (
                  <ImageSlot
                    key={o.optionId}
                    label={o.label}
                    target={{ optionId: o.optionId }}
                    path={o.imagePath}
                    tenantId={tenantId}
                    itemId={item.itemId}
                    onError={onError}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* The override, and it is deliberately last and deliberately quiet. It
          exists for the one combination that genuinely differs and is expected
          to be empty; putting it first would invite somebody to fill in twenty
          slots that the colour level already answers. */}
      {item.variants.filter((v) => v.isActive).length > 1 && (
        <details className="pics__override">
          <summary>One combination that looks different</summary>
          <p className="admin__hint">
            Only for a combination the options above cannot describe. Everything
            here already has a picture — these say where it comes from.
          </p>
          <ul className="pics__list">
            {item.variants
              .filter((v) => v.isActive)
              .map((v) => (
                <li key={v.variantId} className="pics__listrow">
                  <ImageSlot
                    label={v.label}
                    hint={imageSourceLabel(v.image)}
                    target={{ variantId: v.variantId }}
                    // The OWN image, not the resolved one: this control acts on
                    // the override, and offering to "remove" an inherited
                    // picture would clear a slot that was never set.
                    path={v.ownImage?.path ?? null}
                    inheritedPath={v.ownImage ? null : (v.image?.path ?? null)}
                    tenantId={tenantId}
                    itemId={item.itemId}
                    onError={onError}
                    onChanged={onChanged}
                  />
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/**
 * One upload slot.
 *
 * `path` is what this level carries ITSELF; `inheritedPath` is what would show
 * if it carries nothing. Both are rendered and they are visually different,
 * because "this has no picture" and "this has no picture of its own" are
 * different facts and only the first offers a Remove.
 *
 * The file goes through `preparePhoto` — 019's resize, which is also the EXIF
 * strip, inseparably. A swag photograph is unlikely to carry a client site's
 * coordinates, but it comes off somebody's phone and the function costs
 * nothing.
 */
function ImageSlot({
  label,
  hint,
  target,
  path,
  inheritedPath = null,
  tenantId,
  itemId,
  onError,
  onChanged,
}: {
  label: string
  hint?: string
  target: ImageTarget
  path: string | null
  inheritedPath?: string | null
  tenantId: string
  itemId: string
  onError: (m: string | null) => void
  onChanged: () => void
}) {
  const shown = path ?? inheritedPath
  const url = useShopImage(shown)
  const [busy, setBusy] = useState(false)

  async function pick(file: File | undefined) {
    if (!file) return
    setBusy(true)
    onError(null)
    try {
      const prepared = await preparePhoto(file)
      const stored = await uploadShopImage(prepared, tenantId, itemId)
      await setShopImage(target, stored)
      URL.revokeObjectURL(prepared.previewUrl)
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    setBusy(true)
    onError(null)
    try {
      await setShopImage(target, null)
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`pics__slot${path ? ' pics__slot--own' : ''}`}>
      <div className="pics__thumb">
        {url ? (
          // No width/height attributes on a fixed-size thumbnail: the box is the
          // CSS, so there is nothing to reserve. The stored dimensions matter on
          // the shop floor, where the card reserves its box before the picture
          // loads (019), and that is where they are used.
          <img src={url} alt="" />
        ) : (
          <span className="pics__empty" aria-hidden="true">＋</span>
        )}
      </div>

      <p className="pics__label">{label}</p>
      {hint && <p className="pics__hint">{hint}</p>}
      {!path && inheritedPath && <p className="pics__hint">Inherited</p>}

      <label className="btn btn--xs pics__pick">
        {busy ? 'Working…' : path ? 'Replace' : 'Add'}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          // The bucket refuses SVG (070) — an SVG is a script that renders as a
          // picture. The accept attribute is a courtesy on top of that, never
          // the control.
          onChange={(e) => {
            void pick(e.target.files?.[0])
            // Clear it, so picking the same file twice in a row fires again.
            e.target.value = ''
          }}
        />
      </label>

      {path && (
        <button type="button" className="btn btn--xs" disabled={busy} onClick={clear}>
          Remove
        </button>
      )}
    </div>
  )
}

